import { lazy, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Auth } from "@aws-amplify/auth";
import { Amplify } from "@aws-amplify/core";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Grow from "@mui/material/Grow";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as Sentry from "@sentry/react";
import { LicenseManager } from "ag-grid-enterprise";
import { ConfigCatProvider } from "configcat-react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isToday from "dayjs/plugin/isToday";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SnackbarProvider } from "notistack";
import pMinDelay from "p-min-delay";

import FondAdminRoot from "fond-admin/AdminRoot";

import CreateAccount from "fond/accounts/CreateAccount";
import withUserInfo from "fond/accounts/withUserInfo";
import { setAnalyticsItem } from "fond/analytics/storage";
import { selectCurrentAccount } from "fond/api/accountSlice";
import { selectUserId } from "fond/api/userSlice";
import { CityPlannerPage } from "fond/cityPlanner/CityPlannerPage";
import { init as cognitoInit } from "fond/cognito/redux";
import { ACCOUNT_CONTEXT } from "fond/constants";
import { useFeatureFlag } from "fond/featureFlags";
import ForgotPassword from "fond/forgotPassword/ForgotPassword";
import ForgotPasswordSubmit from "fond/forgotPassword/ForgotPasswordSubmit";
import { Impersonate } from "fond/impersonate";
import ImportControllers from "fond/import/controller/ImportControllers";
import InvalidInvitation from "fond/invitation/InvalidInvitation";
import Invitation from "fond/invitation/Invitation";
import { Popout } from "fond/layout";
import Notifier from "fond/notifications/Notifier";
import PrivateRoute from "fond/PrivateRoute";
import PrivateRouteWrapper from "fond/PrivateRouteWrapper";
import ProjectPage from "fond/project/ProjectPage";
import SignIn from "fond/signIn/SignIn";
import SignOut from "fond/signIn/SignOut";
import SignUp from "fond/signUp/SignUp";
import VerifyEmail from "fond/signUp/VerifyEmail";
import { snackbarComponentVariantMap } from "fond/snackbar";
import store from "fond/store";
import { StyleEditorPage } from "fond/styleEditor";
import theme from "fond/theme";
import { UserRoles } from "fond/types";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";
import { setupFilterConsoleWarnings } from "fond/utils/logging";
import { isAppStageIn } from "fond/utils/stages";
import { AccountManagement, Insights, ProfileManagement, UserManagement } from "fond/views";
import CityPlannerFolder from "fond/views/CityPlannerFolder/CityPlannerFolder";
import CityReportFolder from "fond/views/CityReportFolder/CityReportFolder";
import GenericErrorPage from "fond/views/Error/GenericErrorPage";
import NoMatch from "fond/views/Error/NoMatch";
import ResourceNotFound from "fond/views/Error/ResourceNotFound";
import Folders from "fond/views/Folders/Folders";
import MainWorkspace from "fond/views/MainWorkspace/MainWorkspace";
import Recent from "fond/views/Recent/Recent";
import { CreateReport, ImportReport, ReportCost, ReportOverview, ReportRevenue, ReportSettings } from "fond/views/Report";
import PollReportStatus from "fond/views/Report/PollReportStatus/PollReportStatus";
import Settings from "fond/views/Settings/Settings";
import SharedWithMe from "fond/views/Shared/SharedWithMe";
import Starred from "fond/views/Starred/Starred";
import Modal from "fond/widgets/Modal";
import { SuspenseSpinner } from "fond/widgets/SuspenseSpinner";

import "./index.scss";

type ReactComponentModule = () => Promise<{ default: React.FunctionComponent }>;

/**
 * Force a minimum delay when loading views. This is to prevent the view from loading too quickly and causing a flicker
 * After the initial load, the view will load immediately.
 */
const lazyLoadView = (importFactory: ReactComponentModule) => {
  return lazy(() => pMinDelay(importFactory(), 500));
};

const HomeView = lazyLoadView(() => import("fond/views/Home"));
const SubscriptionsView = lazyLoadView(() => import("fond/views/Subscription"));

setupFilterConsoleWarnings([
  // A warning thrown by Mapbox GL JS when an image is not found in the map's style.
  // We do follow their advice and use `map.addImage()` to add the image, so this warning is not relevant.
  "Please make sure you have added the image with map.addImage()",
  // A warning thrown by Stripe.js when the integration is not using HTTPS - this is expected in local development.
  "You may test your Stripe.js integration over HTTP.",
]);

// This is a (hopefully) temporary fix to disable warnings from Atlaskit about its use of their feature flag client.
// See also in vitest.setup.ts
// @ts-expect-error disabling this thing so don't care about type errors
window.__CRITERION__ = { getFeatureFlagOverride: () => false }; // eslint-disable-line no-underscore-dangle

/*
 * Setup the aws amplify library with the correct cognito user pool information.
 */
const awsConfig = {
  Auth: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    // Used for specifying a Cognito endpoint override. Can be left blank for default behavior.
    endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    // Used to change the authentication flow type to "USER_PASSWORD_AUTH" for local Cognito simulation, which is
    // needed to prevent the email confirmation flow, as it's inaccessible via Docker. Can be left blank for default
    // behavior.
    authenticationFlowType: process.env.REACT_APP_AUTHENTICATION_FLOW_TYPE,
  },
};

Amplify.configure(awsConfig);
Auth.configure(awsConfig);

// Initialise dayjs settings / plugins
dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

// Set AgGrid license
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY!);

// Only allow Sentry if we're NOT running locally or running tests.
const allowSentry = !(isAppStageIn(["local", "test"]) || window.location.hostname === "localhost");
if (allowSentry) {
  Sentry.init({
    dsn: "https://47c3988215ff4f299aebf95570e339e3@o312892.ingest.sentry.io/5227566",
    environment: process.env.REACT_APP_STAGE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Capture 1.5% of transaction for performance monitoring.  This is conservative - once we
    // have a better idea of the number of transactions we can bump this rate as appropriate.
    tracesSampleRate: 0.015,
  });
}

/**
 * The root of the application that is mounted after the providers have been
 * mounted.  This allows the content to only be rendered once we have initialised
 * cognito (which requires redux to already be mounted)
 */
const Root = () => {
  const { value: isFondAdminEnabled } = useFeatureFlag("fond_admin");

  const userId = useAppSelector(selectUserId);
  const currentAccount = useAppSelector(selectCurrentAccount);

  // Store the current user's ID in LS so analytics has access to it
  useEffect(() => {
    if (!userId || !currentAccount?.ID) {
      return;
    }

    setAnalyticsItem("userId", userId);
    setAccountContext(userId, currentAccount?.ID);
  }, [userId, currentAccount?.ID]);

  const [init, setInit] = useState(false);
  const [isStale, setIsStale] = useState(false);
  useEffect(() => {
    // Wait for Cognito to initialise, which includes getting the user's
    // attributes (eg. first and last names), if they're logged in, before trying
    // to render anything.
    async function initUser() {
      await cognitoInit(store);
      setInit(true);
    }
    initUser();
  }, []);

  useEffect(() => {
    const showAlert = (e: StorageEvent) => {
      if (e.key === ACCOUNT_CONTEXT) {
        setIsStale(true);
      }
    };
    window.addEventListener("storage", showAlert);

    return () => window.removeEventListener("storage", showAlert);
  }, []);

  if (!init) return null;

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  return (
    <SnackbarProvider
      maxSnack={1}
      TransitionComponent={Grow}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      Components={snackbarComponentVariantMap}
    >
      <Modal
        open={isStale}
        header="Inactive tab, please refresh"
        content="The account selected has been switched in a different tab. Please refresh to see the updated changes."
        actions={
          <Button
            data-testid="refresh-button"
            variant="contained"
            color="primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Refresh
          </Button>
        }
      />
      <Notifier />
      <BrowserRouter future={{ v7_startTransition: false, v7_relativeSplatPath: true }}>
        {/* Render the ImportControllers at top level to support status tracking of imports from any page */}
        <ImportControllers />
        <SentryRoutes>
          <Route path="/" Component={PrivateRouteWrapper}>
            <Route index Component={withUserInfo(HomeView)} />
          </Route>

          <Route path="/" element={<PrivateRoute component={withUserInfo(MainWorkspace)} />} />
          <Route path="/workspace" element={<PrivateRoute component={withUserInfo(MainWorkspace)} />} />
          <Route path="/shared" element={<PrivateRoute component={withUserInfo(SharedWithMe)} />} />
          <Route path="/shared/:account" Component={withUserInfo(SharedWithMe)} />
          <Route path="/recent" element={<PrivateRoute component={withUserInfo(Recent)} />} />
          <Route path="/starred" element={<PrivateRoute component={withUserInfo(Starred)} />} />
          <Route path="/folders/:folderId" element={<PrivateRoute component={withUserInfo(Folders)} />} />

          <Route path="/cities/:multiProjectFolderId" element={<PrivateRoute component={withUserInfo(CityPlannerFolder)} />} />
          <Route path="/city/report/:multiReportFolderId" element={<PrivateRoute component={withUserInfo(CityReportFolder)} />} />
          <Route path="/city/:cityId" element={<PrivateRoute component={withUserInfo(CityPlannerPage)} />} />

          <Route path="/project/:uuid/" element={<PrivateRoute component={withUserInfo(ProjectPage)} />}>
            <Route path=":type/:id/" element={<PrivateRoute component={withUserInfo(ProjectPage)} />} />
          </Route>

          <Route path="/reports/create" element={<PrivateRoute component={withUserInfo(CreateReport)} />} />
          <Route path="/reports/import" element={<PrivateRoute component={withUserInfo(ImportReport)} />} />
          <Route path="/reports/:reportId">
            <Route path="settings" element={<PrivateRoute component={withUserInfo(ReportSettings)} />} />
            <Route path="overview" element={<PrivateRoute component={withUserInfo(ReportOverview)} />} />
            <Route path="cost" element={<PrivateRoute component={withUserInfo(ReportCost)} />} />
            <Route path="revenue" element={<PrivateRoute component={withUserInfo(ReportRevenue)} />} />
            <Route path="poll" element={<PrivateRoute component={withUserInfo(PollReportStatus)} />} />
            <Route index element={<Navigate to="overview" replace />} />
          </Route>

          <Route path="/settings" element={<PrivateRoute component={withUserInfo(Settings)} />} />
          <Route path="/settings/profile/*" element={<PrivateRoute component={withUserInfo(ProfileManagement)} />} />
          <Route path="/styles/:uuid" element={<PrivateRoute component={withUserInfo(StyleEditorPage)} />} />
          <Route path="/settings/account/*" element={<PrivateRoute component={withUserInfo(AccountManagement, UserRoles.admin)} />} />
          <Route path="/settings/users/*" element={<PrivateRoute component={withUserInfo(UserManagement, UserRoles.admin)} />} />
          <Route path="/settings/insights" element={<PrivateRoute component={withUserInfo(Insights, UserRoles.admin)} />} />

          <Route path="/subscription/*" element={<PrivateRoute component={withUserInfo(SubscriptionsView, UserRoles.admin)} />} />

          {isFondAdminEnabled && <Route path="/admin/*" element={<PrivateRoute component={withUserInfo(FondAdminRoot)} />} />}

          <Route path="/signout" element={<SignOut />} />
          <Route path="/signin" element={<SignIn />} />
          {/* old route, just keeping around for old links */}
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/verifyemail" element={<VerifyEmail />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/forgotpasswordsubmit" element={<ForgotPasswordSubmit />} />
          <Route path="/createaccount" element={<PrivateRoute component={withUserInfo(CreateAccount)} />} />
          <Route path="/invitations">
            <Route path="invalid" element={<InvalidInvitation />} />
            <Route path=":invitationId" element={<Invitation />} />
            <Route index element={<NoMatch />} />
          </Route>
          <Route path="/impersonate/:email" element={<Impersonate />} />
          <Route path="/widget" element={<Popout />} />
          {/* catch all route which sends the user to a 'page not found' component */}
          <Route path="/error" element={<GenericErrorPage />} />
          <Route path="/404" element={<ResourceNotFound />} />
          <Route path="*" element={<NoMatch />} />
        </SentryRoutes>
      </BrowserRouter>
    </SnackbarProvider>
  );
};

export const App = (): JSX.Element => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <Provider store={store}>
          <ConfigCatProvider sdkKey={process.env.REACT_APP_CONFIG_CAT_KEY!}>
            <SuspenseSpinner name="root">
              <Root />
            </SuspenseSpinner>
          </ConfigCatProvider>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);
